import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Link2 } from 'lucide-react';

import { RegisterTenantResult, TenantRole, registerTenant } from '../api/tenantRegistry';
import TenantResolveField from '../components/TenantResolveField';
import { useTenantResolve } from '../hooks/useTenantResolve';
import { errorText } from '../lib/errorText';
import { ACCENT, STROKE, SURFACE, TEXT } from '../theme/tokens';

// R-FD-055 / FD-565 — connect a tenant before any campaign names it.
//
// Until R-FD-055 a tenant only existed in this console once a campaign linked it
// (FD-ADR-023 §3), and creating its application refused without one — so a fresh
// Dedicated environment asked for a campaign before it could prepare the tenants
// that campaign needs. This page is the way in: POST /v1/tenants records the tenant
// with the role the operator chooses, then hands over to the tenant's Application
// tab, where the sign-in and the grant happen.
//
// THE ROLE IS THE OPERATOR'S STATEMENT, and at least one is required. It is written
// to the same column campaigns write and is ADDITIVE — connecting an already-known
// tenant can add a role but never remove one, so this page can be used safely on a
// tenant that already exists (the server answers created=false).

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
    maxWidth: '620px',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    color: ACCENT.pressed,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '18px',
    backgroundColor: SURFACE.base,
    padding: '24px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: STROKE.base,
    borderRightColor: STROKE.base,
    borderBottomColor: STROKE.base,
    borderLeftColor: STROKE.base,
  },
  groupLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: TEXT.primary,
  },
  roles: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
  },
  roleHint: {
    marginTop: '0px',
    marginBottom: '0px',
    marginLeft: '30px',
    fontSize: '12px',
    color: TEXT.secondary,
  },
  hint: {
    marginTop: '4px',
    marginBottom: '0px',
    fontSize: '13px',
    color: TEXT.secondary,
  },
  actions: {
    display: 'flex',
    columnGap: '8px',
    justifyContent: 'flex-end',
  },
});

const ROLE_COPY: Record<TenantRole, { label: string; hint: string }> = {
  destination: {
    label: 'Destination',
    hint: 'Devices join this tenant. It needs a minting account and its own application.',
  },
  source: {
    label: 'Source',
    hint: 'Devices leave this tenant (Cloud migrations). Its Intune and Autopilot records are cleaned up.',
  },
};

export default function ConnectTenantPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [value, setValue] = useState('');
  const [roles, setRoles] = useState<Record<TenantRole, boolean>>({
    destination: false,
    source: false,
  });
  const resolve = useTenantResolve(value);

  const chosenRoles = (Object.keys(roles) as TenantRole[]).filter((r) => roles[r]);
  const tenantReady = resolve.kind === 'guid' || resolve.kind === 'resolved';

  const mutation = useMutation<RegisterTenantResult, unknown, void>({
    mutationFn: () =>
      registerTenant({
        ...(resolve.kind === 'guid'
          ? { tenantId: resolve.tenantId }
          : resolve.kind === 'resolved'
            ? { domain: resolve.data.domain, tenantId: resolve.data.tenantId }
            : {}),
        roles: chosenRoles,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(['tenant', result.tenantId], result.tenant);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/tenants/${encodeURIComponent(result.tenantId)}/application`);
    },
  });

  return (
    <div className={styles.page}>
      <Link to="/tenants" className={styles.backLink}>
        <ArrowLeft size={14} strokeWidth={1.5} />
        Back to tenants
      </Link>

      <div>
        <h1 className="font-display text-3xl tracking-tight">Connect a tenant</h1>
        <p className={styles.hint}>
          Record a tenant this environment migrates from or to. Next you&rsquo;ll sign in to it to
          create its application, then grant that application its permissions. Both steps happen
          in Microsoft&rsquo;s sign-in pages and bring you straight back here.
        </p>
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (tenantReady && chosenRoles.length > 0) mutation.mutate();
        }}
      >
        <TenantResolveField label="Tenant" value={value} onChange={setValue} state={resolve} />

        {/* A labelled GROUP, not a Fluent Field. Field forwards its label to its
            first control, which named the Destination box "Role" and left Source
            with no accessible name at all. */}
        <div role="group" aria-labelledby="connect-role-label" aria-describedby="connect-role-hint">
          <div id="connect-role-label" className={styles.groupLabel}>
            Role <span aria-hidden="true">*</span>
          </div>
          <p id="connect-role-hint" className={styles.hint}>
            Choose at least one. A tenant can be both, in different campaigns.
          </p>
          <div className={styles.roles}>
            {(['destination', 'source'] as TenantRole[]).map((role) => (
              <div key={role}>
                <Checkbox
                  label={ROLE_COPY[role].label}
                  checked={roles[role]}
                  onChange={(_, d) => setRoles((prev) => ({ ...prev, [role]: Boolean(d.checked) }))}
                />
                <p className={styles.roleHint}>{ROLE_COPY[role].hint}</p>
              </div>
            ))}
          </div>
        </div>

        {mutation.isError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not connect the tenant</MessageBarTitle>
              {errorText(mutation.error)}
            </MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => navigate('/tenants')}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            type="submit"
            icon={<Link2 size={16} strokeWidth={1.5} />}
            disabled={!tenantReady || chosenRoles.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Connecting…' : 'Connect tenant'}
          </Button>
        </div>
      </form>
    </div>
  );
}
